"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.responsiveCssToMedia = exports.responsiveMapToMedia = exports.breakpointMedia = void 0;
const tokens_1 = require("../../styles/tokens");
const mapPropsToValues_1 = require("../utils/mappers/mapPropsToValues");
const BreakpointConst = ["xs", "sm", "md", "lg", "xl"];
const breakpointMedia = {
    xs: `@media (min-width: ${tokens_1.BREAKPOINTS.xs})`,
    sm: `@media (min-width: ${tokens_1.BREAKPOINTS.sm})`,
    md: `@media (min-width: ${tokens_1.BREAKPOINTS.md})`,
    lg: `@media (min-width: ${tokens_1.BREAKPOINTS.lg})`,
    xl: `@media (min-width: ${tokens_1.BREAKPOINTS.xl})`,
};
exports.breakpointMedia = breakpointMedia;
/**
 * This helper will convert responsive props values (propValue) to CSS accoring to a mapping object (mapping)
 * It is used for example with props of type Responsive<"medium" | "large">
 *
 * ***** Example 1 *****
 * propValue="medium"
 * mapping={ "medium": { padding: "0" }, "large": { padding: "10px" } }
 * Returns:
 * {
 *   padding: "0"
 * }
 *
 * ***** Example 2 *****
 * propValue="{ xs: "medium", xl: "large" }"
 * mapping={ "medium": { padding: "0" }, "large": { padding: "10px" } }
 * Returns:
 * {
 *   "@media (min-width: 0)": {
 *      padding: "0"
 *   },
 *   "@media (min-width: 1200px)": {
 *      padding: "10px"
 *   }
 * }
 * @param propValue value passed in the component, ie: "medium", "large", "{ sm: "medium", xl: "large" }"
 * @param mapping expected css for each possible propValue
 */
const responsiveMapToMedia = (propValue, mapping) => {
    // Handle direct values (ie: size="medium", size="large")
    if (typeof propValue === "string") {
        return mapping[propValue];
    }
    // Handle responsive values (ie: size={{ sm: "medium", xl: "large" }}) by adding media queries
    const mediaCssObject = {};
    Object.entries(propValue).forEach(([key, value]) => {
        assertIsBreakpoint(key);
        mediaCssObject[breakpointMedia[key]] = mapping[value];
    });
    return mediaCssObject;
};
exports.responsiveMapToMedia = responsiveMapToMedia;
/**
 * This helper will add media queries to a responsive props values (propValue)
 * It is used for example with CSS props of type Responsive<"row" | "row-reverse" | "column" | "column-reverse">
 * @param value value passed in the component, ie: "column", "row-reverse", "{ sm: "column", xl: "row" }"
 * @param property ie: "flex-direction", "align-items"
 * @returns Corresponding CssObject for styled components
 *
 * ***** Example 1 *****
 * Example of returned CssObject from a non responsive values
 * propValue="row"
 * cssRule="flex-direction"
 * Returns:
 * {
 *   flex-direction: "row"
 * }
 *
 * ***** Example 2 *****
 * Example of returned CssObject from responsive values
 * value="{ xs: "column", xl: "row" }"
 * property="flex-direction"
 * Returns:
 * {
 *      "@media (min-width: 0)": {
 *          "flex-direction": "column"
 *      }
 *      "@media (min-width: 1200px)": {
 *          "flex-direction": "row"
 *      }
 * }
 */
const responsiveCssToMedia = (value, property) => {
    // Handle non responsive values (ie: flexDirection="column" )
    if (typeof value !== "object") {
        return {
            [property]: (0, mapPropsToValues_1.mapPropsToValues)({ value, property }),
        };
    }
    // Handle responsive values (ie: flexDirection={{ sm: "column", xl: "row" }}) by adding media queries
    const mediaCssObject = {};
    Object.entries(value).forEach(([key, value]) => {
        assertIsBreakpoint(key);
        mediaCssObject[breakpointMedia[key]] = {
            [property]: (0, mapPropsToValues_1.mapPropsToValues)({ value, property }),
        };
    });
    return mediaCssObject;
};
exports.responsiveCssToMedia = responsiveCssToMedia;
/**
 * Assert that we have given a valid value of type Breakpoint (ie: "xs", "sm")
 * or throw and error
 */
function assertIsBreakpoint(value) {
    if (BreakpointConst.some((breakpoint) => breakpoint === value)) {
        return;
    }
    throw new Error(`${value} should only be of type "Breakpoint"`);
}
//# sourceMappingURL=Responsive.js.map